import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  FormControlLabel,
  FormHelperText,
  Switch,
  DialogContentText,
  Divider,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SnoozeIcon from '@mui/icons-material/Snooze';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import HabitQuickLogChips, {
  HabitMeasurementKind,
  formatMeasureValue,
  isMeasurableKind,
  quickIncrementValues,
} from '../components/HabitQuickLogChips';
import HabitHistoryDialog from '../components/HabitHistoryDialog';
import HabitExportDialog from '../components/HabitExportDialog';
import HabitBulkEditDialog from '../components/HabitBulkEditDialog';
import CheckInWindowsEditor from '../components/CheckInWindowsEditor';
import CheckInOptionsEditor from '../components/CheckInOptionsEditor';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import { usePersistentViewMode } from '../hooks/usePersistentViewMode';
import { useHabitColumnPreferences } from '../hooks/useHabitColumnPreferences';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import {
  DEFAULT_HABITS_FILTER_STATE,
  DEFAULT_USER_SETTINGS,
  HABITS_FILTER_PRESET_LIMIT,
  HabitCheckInFilter,
  HabitProgressTodayFilter,
  HabitSortKey,
  HabitStatusFilter,
  HabitTitleClickAction,
  HabitsFilterPreset,
  HabitsFilterState,
  loadHabitsFiltersSettings,
  usePreferences,
} from '../contexts/PreferencesContext';

type HabitRecurrence = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

// Habit catch-up config (migration 0029). Mode/reach drive only what the
// check-in queue offers as catch-up items and the UI picker bounds — the
// write paths stay unbounded (owner decision Q4). The UI copy deliberately
// never uses the bare word "Backfill": that vocabulary belongs to the
// time-entry wizard.
type HabitBackfillModeValue = 'none' | 'retroactive_log' | 'debt_carry_forward';
type HabitCarryoverPolicyValue = 'stack' | 'merge';

// Progress inside the recurrence period covering "today" — attached per habit
// by ?include_today=true. period_end is EXCLUSIVE (first day of the next
// period). remaining_value is ALWAYS null for OPAQUE habits, whose values are
// never interpreted, not even by subtraction.
export interface HabitPeriodProgress {
  period_start: string;
  period_end: string;
  days_left: number;
  target_count: number;
  target_value?: number | null;
  logged_count: number;
  logged_value: number;
  remaining_count: number;
  remaining_value?: number | null;
}

export interface Habit {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  recurrence_type: HabitRecurrence;
  recurrence_interval: number;
  target_count: number;
  is_active: boolean;
  snoozed_until?: string | null;
  // Set when soft-archived; null/absent for live habits.
  archived_at?: string | null;
  // Measurable-habit fields (migration 0022). unit_label is the ONLY display
  // unit; the legacy import-provenance `unit` field must never be rendered.
  measurement_kind?: HabitMeasurementKind | string;
  unit_label?: string | null;
  target_value?: number | null;
  batch_size?: number | null;
  quick_increments?: number[] | null;
  check_in_enabled?: boolean;
  check_in_priority?: number;
  // Score preset currently stamped onto this habit's check-in options
  // (migration 0028). Read-only on habit endpoints — assignment goes through
  // PUT /api/check-ins/habits/{id}/score-preset.
  score_preset_id?: number | null;
  // Catch-up config (migration 0029). backfill_reach_days null = the mode
  // default (retro 7, debt 35).
  backfill_mode?: HabitBackfillModeValue | string;
  backfill_reach_days?: number | null;
  carryover_policy?: HabitCarryoverPolicyValue | string;
  tags?: string[];
  // Populated only when fetched with ?include_today=true.
  today_completed_count?: number;
  is_due_now?: boolean;
  is_snoozed?: boolean;
  period_progress?: HabitPeriodProgress | null;
  created_at: string;
  updated_at?: string;
}

interface HabitFormData {
  title: string;
  description: string;
  recurrence_type: HabitRecurrence;
  recurrence_interval: number;
  target_count: number;
  is_active: boolean;
  measurement_kind: HabitMeasurementKind;
  // Kept as strings while editing so the fields can be emptied; converted to
  // numbers/null when the payload is built.
  unit_label: string;
  target_value: string;
  batch_size: string;
  quick_increments: number[];
  check_in_enabled: boolean;
  check_in_priority: number;
  // '' = no preset ("None" in the select); otherwise the preset id.
  score_preset_id: number | '';
  backfill_mode: HabitBackfillModeValue;
  // String while editing so the field can be emptied ('' = mode default).
  backfill_reach_days: string;
  carryover_policy: HabitCarryoverPolicyValue;
  tags: string[];
}

// Minimal slice of GET /api/check-ins/score-presets used by the dialog.
interface ScorePresetSummary {
  id: number;
  name: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// List-view columns the user can hide. The Habit (identity), Actions, and
// bulk-select columns are always shown and are not part of this set.
const HIDEABLE_COLUMNS = [
  { key: 'recurrence', label: 'Recurrence' },
  { key: 'today', label: 'Today' },
  { key: 'status', label: 'Status' },
  { key: 'checkin', label: 'Check-in' },
];
const HIDEABLE_COLUMN_KEYS = HIDEABLE_COLUMNS.map((column) => column.key);

// What clicking a habit's title does — the server-backed account preference
// settings.habit_title_click_action. The explicit row/card action buttons all
// stay; the title click is only an accelerator for one of them.
const TITLE_CLICK_OPTIONS: { value: HabitTitleClickAction; label: string }[] = [
  { value: 'stats', label: 'Open stats' },
  { value: 'history', label: 'Open history' },
  { value: 'edit', label: 'Edit habit' },
  { value: 'log', label: 'Mark done' },
  { value: 'none', label: 'Do nothing' },
];

const TITLE_CLICK_ARIA_PREFIX: Record<Exclude<HabitTitleClickAction, 'none'>, string> = {
  stats: 'Open stats for',
  history: 'Open history for',
  edit: 'Open editor for',
  log: 'Mark done for',
};

// SQL-side sort keys, kept in lockstep with the backend's HABIT_SORT_KEYS
// allow-list (off-list values 422). '' = the list's default ordering.
const SORT_OPTIONS: { value: HabitSortKey; label: string }[] = [
  { value: 'title', label: 'Title' },
  { value: 'frequency', label: 'Frequency' },
  { value: 'progress_today', label: 'Progress today' },
  { value: 'next_due', label: 'Next due' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'check_in_priority', label: 'Check-in priority' },
];

/** The backend's per-key default direction: most progress first, everything
 * else ascending (soonest due / A-first / oldest created). */
const defaultOrderFor = (key: HabitSortKey): 'asc' | 'desc' =>
  key === 'progress_today' ? 'desc' : 'asc';

const defaultFormData: HabitFormData = {
  title: '',
  description: '',
  recurrence_type: 'daily',
  recurrence_interval: 1,
  target_count: 1,
  is_active: true,
  measurement_kind: 'binary',
  unit_label: '',
  target_value: '',
  batch_size: '',
  quick_increments: [],
  check_in_enabled: true,
  check_in_priority: 0,
  score_preset_id: '',
  backfill_mode: 'none',
  backfill_reach_days: '',
  carryover_policy: 'merge',
  tags: [],
};

// One-line helper text per measurement kind (dialog). The OPAQUE wording is
// the ratified spec text verbatim.
const MEASUREMENT_KIND_OPTIONS: {
  value: HabitMeasurementKind;
  label: string;
  helper: string;
}[] = [
  {
    value: 'binary',
    label: 'Binary',
    helper: 'A plain done / not-done habit — no numbers beyond the target count.',
  },
  {
    value: 'count',
    label: 'Count',
    helper: 'Whole-number repetitions per period (e.g. 108 recitations).',
  },
  {
    value: 'duration',
    label: 'Duration',
    helper: 'A measured amount of time (e.g. minutes of practice).',
  },
  {
    value: 'quantity',
    label: 'Quantity',
    helper: 'A measured amount in any unit (e.g. km, pages, words).',
  },
  {
    value: 'opaque',
    label: 'Opaque',
    helper:
      'Stores your exact numbers without interpreting them — completion is simply value ≥ target. Use the description to document your private scheme.',
  },
];

// Shape of POST /api/habits/{id}/measurement-conversion responses.
interface MeasurementConversionResult {
  habit_id: number;
  preview: boolean;
  multiplier: number;
  total_log_rows: number;
  affected_log_rows: number;
  samples: {
    date: string;
    before_count: number;
    before_value?: number | null;
    after_count: number;
    after_value: number;
  }[];
  operation_id?: number | null;
}

/** Days spanned by [start, end) — period_end is exclusive. */
const periodSpanDays = (start: string, end: string): number =>
  Math.max(1, Math.round((Date.parse(end) - Date.parse(start)) / 86400000));

// Trim, lowercase, and de-dupe tag entries — the same normalization the
// backend applies (schemas._normalize_habit_tags), mirrored client-side so
// chips show what will actually be stored. Same helper as AddTimerDialog.
const normalizeTags = (values: string[]): string[] => {
  const seen = new Set<string>();
  const tags: string[] = [];
  values.forEach((raw) => {
    const tag = raw.trim().toLowerCase();
    if (tag && !seen.has(tag)) {
      seen.add(tag);
      tags.push(tag);
    }
  });
  return tags;
};

const recurrenceLabel: Record<HabitRecurrence, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  custom: 'Custom',
};

const recurrenceColor = (
  type: HabitRecurrence,
): 'primary' | 'secondary' | 'success' | 'warning' | 'default' => {
  if (type === 'daily') return 'success';
  if (type === 'weekly') return 'primary';
  if (type === 'monthly') return 'secondary';
  if (type === 'yearly') return 'warning';
  return 'default';
};

// Snooze presets — kept in lockstep with the server-side allow-list in
// schemas.HABIT_SNOOZE_PRESET_MINUTES. Off-list values 422 from the API.
export const SNOOZE_PRESETS: { label: string; minutes: number }[] = [
  { label: '40 min', minutes: 40 },
  { label: '3 hours', minutes: 180 },
  { label: '4 hours', minutes: 240 },
  { label: '7 hours', minutes: 420 },
  { label: '13 hours', minutes: 780 },
  { label: '30 hours', minutes: 1800 },
  { label: '40 hours', minutes: 2400 },
];

/** Render "2h 14m" / "37m" / "Snoozed" for the live countdown chip. */
const formatRemaining = (msUntil: number): string => {
  if (msUntil <= 0) return 'Expiring…';
  const totalMinutes = Math.floor(msUntil / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

interface HabitTitleProps {
  habit: Habit;
  action: HabitTitleClickAction;
  variant: 'h6' | 'body2';
  onActivate: (habit: Habit) => void;
}

/** The habit's name in a card or row. With a title-click action configured it
 * renders as a real button (keyboard-focusable, labelled with the action);
 * with 'none' it stays plain text. */
const HabitTitle: React.FC<HabitTitleProps> = ({ habit, action, variant, onActivate }) => {
  const isHeading = variant === 'h6';
  if (action === 'none') {
    return (
      <Typography
        variant={variant}
        sx={{ fontWeight: 600 }}
        noWrap={isHeading}
        title={isHeading ? habit.title : undefined}
      >
        {habit.title}
      </Typography>
    );
  }
  return (
    <Link
      component="button"
      type="button"
      variant={variant}
      color="inherit"
      underline="hover"
      noWrap={isHeading}
      title={isHeading ? habit.title : undefined}
      onClick={() => onActivate(habit)}
      aria-label={`${TITLE_CLICK_ARIA_PREFIX[action]} ${habit.title}`}
      sx={{ fontWeight: 600, textAlign: 'left', maxWidth: '100%' }}
    >
      {habit.title}
    </Link>
  );
};

const Habits: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const navigate = useNavigate();
  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('habits');
  const { isColumnVisible, toggleColumn, resetToDefault: resetColumns } =
    useHabitColumnPreferences(HIDEABLE_COLUMN_KEYS);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [habitToDelete, setHabitToDelete] = useState<Habit | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editHabit, setEditHabit] = useState<Habit | null>(null);
  // Per-habit response option editor (edit mode only — a new habit has no id).
  const [optionsEditorOpen, setOptionsEditorOpen] = useState(false);
  const [formData, setFormData] = useState<HabitFormData>(defaultFormData);
  const [quickIncrementDraft, setQuickIncrementDraft] = useState('');
  // True after a switch to OPAQUE forcibly reset a chosen debt catch-up mode
  // (owed amounts need arithmetic; opaque values never get any) — renders the
  // one-line explanation under the mode select.
  const [debtModeResetNote, setDebtModeResetNote] = useState(false);

  // Conversion helper (edit dialog only): preview → explicit commit.
  const [conversionMultiplier, setConversionMultiplier] = useState('');
  const [conversionPreview, setConversionPreview] =
    useState<MeasurementConversionResult | null>(null);
  const [conversionDone, setConversionDone] =
    useState<MeasurementConversionResult | null>(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);

  const [historyHabit, setHistoryHabit] = useState<Habit | null>(null);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [scorePresets, setScorePresets] = useState<ScorePresetSummary[]>([]);

  const preferences = usePreferences();
  // First-render snapshot of the persisted sort/filter state. The provider
  // hydrates synchronously from its cache, so a returning user's state is
  // already here; adopting later server updates mid-session would stomp on
  // in-progress edits, so the snapshot is deliberately taken once.
  const [initialFilterState] = useState<HabitsFilterState>(() => {
    const stored = preferences
      ? loadHabitsFiltersSettings(preferences.settings.habits_filters).active
      : null;
    return stored ?? DEFAULT_HABITS_FILTER_STATE;
  });

  const [tagFilter, setTagFilter] = useState<string[]>(initialFilterState.tags);
  const [tagMode, setTagMode] = useState<'any' | 'all'>(initialFilterState.tag_mode);
  // Which archive slice to show. 'active' (the default) matches the server
  // default (?status omitted); 'archived' surfaces shelved habits for review
  // and one-click restore. 'all' is reachable only via a stored preset.
  const [statusFilter, setStatusFilter] = useState<HabitStatusFilter>(
    initialFilterState.status,
  );
  const [sortKey, setSortKey] = useState<HabitSortKey | null>(initialFilterState.sort);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    initialFilterState.order ??
      (initialFilterState.sort ? defaultOrderFor(initialFilterState.sort) : 'asc'),
  );
  const [progressFilter, setProgressFilter] = useState<HabitProgressTodayFilter | ''>(
    initialFilterState.progress_today ?? '',
  );
  const [checkInFilter, setCheckInFilter] = useState<HabitCheckInFilter | ''>(
    initialFilterState.check_in ?? '',
  );
  // Free-text search: the input updates per keystroke, the query (and the
  // refetch it triggers) trails it by a short debounce.
  const [searchInput, setSearchInput] = useState(initialFilterState.q);
  const [searchQuery, setSearchQuery] = useState(initialFilterState.q);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetNameDraft, setPresetNameDraft] = useState('');
  const [selectedHabitIds, setSelectedHabitIds] = useState<number[]>([]);
  const [exportHabitIds, setExportHabitIds] = useState<number[]>([]);
  const [exportDialogTitle, setExportDialogTitle] = useState<string | undefined>(undefined);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const [snoozeMenuAnchor, setSnoozeMenuAnchor] = useState<HTMLElement | null>(null);
  const [snoozeMenuHabit, setSnoozeMenuHabit] = useState<Habit | null>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);
  const [moreMenuHabit, setMoreMenuHabit] = useState<Habit | null>(null);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<HTMLElement | null>(null);
  const [titleActionMenuAnchor, setTitleActionMenuAnchor] =
    useState<HTMLElement | null>(null);

  const titleClickAction: HabitTitleClickAction =
    preferences?.settings.habit_title_click_action ??
    DEFAULT_USER_SETTINGS.habit_title_click_action;

  // 30-second tick that re-renders every card so the "Snoozed · 1h 23m"
  // chips count down without each card owning its own timer.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const fetchHabits = async () => {
    try {
      setLoading(true);
      const tzOffset = new Date().getTimezoneOffset();
      const params = new URLSearchParams({
        include_today: 'true',
        tz_offset: String(tzOffset),
      });
      if (tagFilter.length > 0) {
        params.set('tags', tagFilter.join(','));
        params.set('tag_mode', tagMode);
      }
      if (statusFilter !== 'active') {
        params.set('status', statusFilter);
      }
      if (sortKey) {
        params.set('sort', sortKey);
        params.set('order', sortOrder);
      }
      if (progressFilter) {
        params.set('progress_today', progressFilter);
      }
      if (checkInFilter) {
        params.set('check_in', checkInFilter);
      }
      if (searchQuery.trim()) {
        params.set('q', searchQuery.trim());
      }
      const response = await axios.get(
        `${API_BASE_URL}/api/habits/?${params.toString()}`,
      );
      setHabits(response.data);
      setSelectedHabitIds((prev) =>
        prev.filter((id) => response.data.some((habit: Habit) => habit.id === id)),
      );
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load habits'));
    } finally {
      setLoading(false);
    }
  };

  // The user's existing tags (from the tag-management API) power both the
  // dialog's suggestions and the filter's options, so a save reuses a tag
  // instead of drifting into near-duplicates. A failed request degrades to
  // no suggestions — freeSolo entry still works.
  const fetchTagOptions = () => {
    axios
      .get(`${API_BASE_URL}/api/habit-tags/`)
      .then((response) => {
        const names = Array.isArray(response.data)
          ? response.data
              .map((tag: { name?: unknown }) => tag?.name)
              .filter(
                (name: unknown): name is string =>
                  typeof name === 'string' && name.length > 0,
              )
          : [];
        setTagOptions(names);
      })
      .catch(() => setTagOptions([]));
  };

  // Refetch whenever a sort/filter dimension changes; the initial mount fetch
  // is the empty-filter case of the same effect. fetchHabits is re-created per
  // render and reads the current filter state, so it stays out of the deps
  // (repo precedent: Reflections.tsx, usePersistentViewMode).
  useEffect(() => {
    fetchHabits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tagFilter,
    tagMode,
    statusFilter,
    sortKey,
    sortOrder,
    progressFilter,
    checkInFilter,
    searchQuery,
  ]);

  // Debounce free-text search so the list doesn't refetch per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setSearchQuery(searchInput), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    fetchTagOptions();
  }, []);

  // -- Sort/filter persistence + presets (habits_filters namespace) ---------
  // The namespace is replaced wholesale on every write (see UserSettingsUpdate
  // semantics), so every write carries the complete {active, presets} value.

  const habitsFiltersNamespace = useMemo(
    () => loadHabitsFiltersSettings(preferences?.settings.habits_filters ?? null),
    [preferences?.settings.habits_filters],
  );
  const filterPresets = habitsFiltersNamespace.presets;

  const currentFilterState = (): HabitsFilterState => ({
    sort: sortKey,
    order: sortKey ? sortOrder : null,
    status: statusFilter,
    tags: tagFilter,
    tag_mode: tagMode,
    progress_today: progressFilter || null,
    check_in: checkInFilter || null,
    q: searchQuery.trim(),
  });

  // Persist the live state once the user changes something. The mount pass is
  // skipped: restoring stored state must not immediately re-PUT it.
  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!preferences) return;
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    preferences.setNamespace('habits_filters', {
      ...habitsFiltersNamespace,
      active: currentFilterState(),
    } as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tagFilter,
    tagMode,
    statusFilter,
    sortKey,
    sortOrder,
    progressFilter,
    checkInFilter,
    searchQuery,
  ]);

  const writeHabitsFilters = (presets: HabitsFilterPreset[]) => {
    if (!preferences) return;
    preferences.setNamespace('habits_filters', {
      ...habitsFiltersNamespace,
      active: currentFilterState(),
      presets,
    } as unknown as Record<string, unknown>);
  };

  const applyFilterPreset = (preset: HabitsFilterPreset) => {
    setTagFilter([...preset.tags]);
    setTagMode(preset.tag_mode);
    setStatusFilter(preset.status);
    setSortKey(preset.sort);
    setSortOrder(
      preset.order ?? (preset.sort ? defaultOrderFor(preset.sort) : 'asc'),
    );
    setProgressFilter(preset.progress_today ?? '');
    setCheckInFilter(preset.check_in ?? '');
    setSearchInput(preset.q);
    setSearchQuery(preset.q);
  };

  const saveFilterPreset = () => {
    const name = presetNameDraft.trim();
    if (!name || filterPresets.length >= HABITS_FILTER_PRESET_LIMIT) return;
    const preset: HabitsFilterPreset = {
      id: `preset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      ...currentFilterState(),
    };
    writeHabitsFilters([...filterPresets, preset]);
    setPresetDialogOpen(false);
    setPresetNameDraft('');
  };

  const deleteFilterPreset = (id: string) => {
    writeHabitsFilters(filterPresets.filter((preset) => preset.id !== id));
  };

  // The user's score presets power the dialog's "Score preset" select
  // (fetched on every dialog open so a preset created in another tab shows
  // up). A failed request degrades silently to an empty list — the select
  // then offers only "None".
  const fetchScorePresets = () => {
    axios
      .get(`${API_BASE_URL}/api/check-ins/score-presets`)
      .then((response) => {
        const rows: ScorePresetSummary[] = Array.isArray(response.data)
          ? response.data.filter(
              (preset: { id?: unknown; name?: unknown }) =>
                typeof preset?.id === 'number' && typeof preset?.name === 'string',
            )
          : [];
        setScorePresets(rows);
      })
      .catch(() => setScorePresets([]));
  };

  const resetConversionState = () => {
    setConversionMultiplier('');
    setConversionPreview(null);
    setConversionDone(null);
    setConversionLoading(false);
    setConversionError(null);
  };

  const openCreateDialog = () => {
    setEditHabit(null);
    setOptionsEditorOpen(false);
    setFormData(defaultFormData);
    setQuickIncrementDraft('');
    setDebtModeResetNote(false);
    resetConversionState();
    fetchTagOptions();
    fetchScorePresets();
    setDialogOpen(true);
  };

  const openEditDialog = (habit: Habit) => {
    setEditHabit(habit);
    setOptionsEditorOpen(false);
    setFormData({
      title: habit.title,
      description: habit.description || '',
      recurrence_type: habit.recurrence_type,
      recurrence_interval: habit.recurrence_interval,
      target_count: habit.target_count,
      is_active: habit.is_active,
      measurement_kind: (habit.measurement_kind as HabitMeasurementKind) || 'binary',
      unit_label: habit.unit_label || '',
      target_value: habit.target_value != null ? String(habit.target_value) : '',
      batch_size: habit.batch_size != null ? String(habit.batch_size) : '',
      quick_increments: [...(habit.quick_increments || [])],
      check_in_enabled: habit.check_in_enabled ?? false,
      check_in_priority: habit.check_in_priority ?? 0,
      score_preset_id: habit.score_preset_id ?? '',
      backfill_mode: (habit.backfill_mode as HabitBackfillModeValue) || 'none',
      backfill_reach_days:
        habit.backfill_reach_days != null ? String(habit.backfill_reach_days) : '',
      carryover_policy:
        (habit.carryover_policy as HabitCarryoverPolicyValue) || 'merge',
      tags: [...(habit.tags || [])],
    });
    setQuickIncrementDraft('');
    setDebtModeResetNote(false);
    resetConversionState();
    fetchTagOptions();
    fetchScorePresets();
    setDialogOpen(true);
  };

  // The measurement fields as the API expects them, from the current form
  // state. OPAQUE deliberately carries no unit/batch/quick-increments — its
  // values are never interpreted — and BINARY has nothing to measure.
  const measurementFieldsFromForm = () => {
    const kind = formData.measurement_kind;
    const measurable = isMeasurableKind(kind);
    return {
      measurement_kind: kind,
      unit_label:
        measurable && formData.unit_label.trim() ? formData.unit_label.trim() : null,
      target_value:
        kind !== 'binary' && formData.target_value !== ''
          ? Number(formData.target_value)
          : null,
      batch_size:
        measurable && formData.batch_size !== '' ? Number(formData.batch_size) : null,
      quick_increments:
        measurable && formData.quick_increments.length > 0
          ? formData.quick_increments
          : null,
    };
  };

  /** 'How far back (days)' as the API expects it: whole days, capped at 365.
   * Empty, 0, anything below 1, unparseable text — and mode "none" — all
   * degrade to null, which means "use the mode default" server-side; a typed
   * 0 is a request for the default, never silently promoted to 1 day. */
  const reachDaysFromForm = (): number | null => {
    if (formData.backfill_mode === 'none') return null;
    const raw = formData.backfill_reach_days.trim();
    if (raw === '') return null;
    const floored = Math.floor(Number(raw));
    if (!Number.isFinite(floored) || floored < 1) return null;
    return Math.min(365, floored);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Habit title is required');
      return;
    }
    if (formData.recurrence_interval < 1 || formData.target_count < 1) {
      setError('Recurrence interval and target count must be at least 1');
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      recurrence_type: formData.recurrence_type,
      recurrence_interval: formData.recurrence_interval,
      target_count: formData.target_count,
      is_active: formData.is_active,
      check_in_enabled: formData.check_in_enabled,
      check_in_priority: formData.check_in_priority,
      backfill_mode: formData.backfill_mode,
      backfill_reach_days: reachDaysFromForm(),
      carryover_policy: formData.carryover_policy,
      tags: formData.tags,
      ...measurementFieldsFromForm(),
    };

    try {
      setSaveLoading(true);
      let savedHabitId: number | null;
      if (editHabit) {
        await axios.put(`${API_BASE_URL}/api/habits/${editHabit.id}`, payload);
        savedHabitId = editHabit.id;
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/habits/`, payload);
        savedHabitId = response.data?.id ?? null;
      }
      // Score preset assignment is a separate endpoint — it stamps the
      // preset's answers onto the habit's options (never deleting custom
      // ones) — so it only runs AFTER the save succeeds and only when the
      // choice actually changed: re-saving an untouched habit must not
      // re-stamp. Its failure must NOT read as "save failed": the habit row
      // already exists, so the dialog still closes (leaving it open invites
      // a retry that would create the habit twice) and the partial failure
      // is reported honestly through the page error surface.
      const chosenPresetId =
        formData.score_preset_id === '' ? null : formData.score_preset_id;
      const baselinePresetId = editHabit ? editHabit.score_preset_id ?? null : null;
      let presetAssignmentError: string | null = null;
      if (savedHabitId != null && chosenPresetId !== baselinePresetId) {
        try {
          await axios.put(
            `${API_BASE_URL}/api/check-ins/habits/${savedHabitId}/score-preset`,
            { preset_id: chosenPresetId },
          );
        } catch (assignErr: any) {
          presetAssignmentError = getApiErrorMessage(
            assignErr,
            'the assignment request failed',
          );
        }
      }
      setDialogOpen(false);
      await fetchHabits();
      // A save may have minted a new tag — keep suggestions/filter fresh.
      fetchTagOptions();
      if (presetAssignmentError) {
        // After fetchHabits — a successful refetch clears the error slot.
        setError(
          `Habit saved — score preset assignment failed: ${presetAssignmentError}`,
        );
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save habit'));
    } finally {
      setSaveLoading(false);
    }
  };

  // -- Measurement conversion (edit dialog) ---------------------------------
  // One-time preview→commit flow for switching an existing habit to a
  // measurable kind: history is multiplied into the new units server-side.
  // Only offered while EDITING with the kind switched to count/duration/
  // quantity; never on create (no history), never for opaque/binary targets.

  const conversionOffered =
    editHabit !== null &&
    conversionDone === null &&
    isMeasurableKind(formData.measurement_kind) &&
    formData.measurement_kind !== (editHabit.measurement_kind || 'binary');

  const runConversion = async (preview: boolean) => {
    if (!editHabit) return;
    const multiplier = Number(conversionMultiplier);
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      setConversionError('Enter a positive multiplier');
      return;
    }
    const targetValue = Number(formData.target_value);
    if (!Number.isFinite(targetValue) || targetValue <= 0) {
      setConversionError('Set a positive target value above first');
      return;
    }
    try {
      setConversionLoading(true);
      setConversionError(null);
      const response = await axios.post(
        `${API_BASE_URL}/api/habits/${editHabit.id}/measurement-conversion`,
        // The payload IS the habit's new measurement configuration — the
        // commit sets these fields verbatim (unset optionals clear).
        { multiplier, ...measurementFieldsFromForm(), preview },
      );
      if (preview) {
        setConversionPreview(response.data);
      } else {
        setConversionPreview(null);
        setConversionDone(response.data);
        // The server already applied the measurement fields; realign the
        // dialog's baseline so the offer disappears and lists refresh.
        setEditHabit((prev) =>
          prev ? { ...prev, ...measurementFieldsFromForm() } : prev,
        );
        await fetchHabits();
      }
    } catch (err: any) {
      setConversionError(getApiErrorMessage(err, 'Failed to convert measurements'));
    } finally {
      setConversionLoading(false);
    }
  };

  const cancelConversion = () => {
    setConversionPreview(null);
    setConversionMultiplier('');
    setConversionError(null);
  };

  const addQuickIncrement = () => {
    const parsed = Number(quickIncrementDraft);
    if (
      !Number.isFinite(parsed) ||
      parsed <= 0 ||
      formData.quick_increments.length >= 8
    ) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      quick_increments: [...prev.quick_increments, parsed],
    }));
    setQuickIncrementDraft('');
  };

  const handleDelete = async () => {
    if (!habitToDelete) return;

    try {
      setDeleteLoading(true);
      await axios.delete(`${API_BASE_URL}/api/habits/${habitToDelete.id}`);
      setDeleteDialogOpen(false);
      setHabitToDelete(null);
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete habit'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // -- Archive / restore ----------------------------------------------------
  // Reversible by design, so no confirmation dialog (unlike delete, which
  // keeps its "cannot be undone" confirmation).

  const handleArchive = async (habit: Habit) => {
    try {
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/archive`);
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to archive habit'));
    }
  };

  const handleRestore = async (habit: Habit) => {
    try {
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/restore`);
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to restore habit'));
    }
  };

  // -- Done / +N actions ----------------------------------------------------

  /** YYYY-MM-DD for the user's current local calendar day. Always send
   * this on quick actions so the server doesn't have to guess "today"
   * from a UTC instant — the prior bug class for non-UTC users. */
  const todayLocalYmd = (): string => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(
      t.getDate(),
    ).padStart(2, '0')}`;
  };

  const handleIncrement = async (habit: Habit) => {
    try {
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/log/increment`, {
        amount: 1,
        date_local: todayLocalYmd(),
      });
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to log habit'));
    }
  };

  const handleMarkComplete = async (habit: Habit) => {
    try {
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/log/complete`, {
        date_local: todayLocalYmd(),
      });
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to mark habit done'));
    }
  };

  const handleClearToday = async (habit: Habit) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/habits/${habit.id}/log`, {
        params: { date: todayLocalYmd() },
      });
      await fetchHabits();
    } catch (err: any) {
      // 404 means there was nothing to clear — treat that as a no-op rather
      // than a hard error so the button stays forgiving.
      if (err?.response?.status !== 404) {
        setError(getApiErrorMessage(err, 'Failed to clear habit log'));
      } else {
        await fetchHabits();
      }
    }
  };

  /** Dispatch the configured title-click accelerator. Every target also keeps
   * its explicit button, so this never becomes the only path to an action. */
  const handleTitleActivate = (habit: Habit) => {
    if (titleClickAction === 'stats') {
      navigate(`/habits/stats/${habit.id}`);
    } else if (titleClickAction === 'history') {
      setHistoryHabit(habit);
    } else if (titleClickAction === 'edit') {
      openEditDialog(habit);
    } else if (titleClickAction === 'log') {
      handleMarkComplete(habit);
    }
  };

  // -- Snooze ---------------------------------------------------------------

  const openSnoozeMenu = (event: React.MouseEvent<HTMLElement>, habit: Habit) => {
    setSnoozeMenuAnchor(event.currentTarget);
    setSnoozeMenuHabit(habit);
  };

  const closeSnoozeMenu = () => {
    setSnoozeMenuAnchor(null);
    setSnoozeMenuHabit(null);
  };

  const handleSnooze = async (minutes: number) => {
    if (!snoozeMenuHabit) return;
    const habit = snoozeMenuHabit;
    closeSnoozeMenu();
    try {
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/snooze`, {
        duration_minutes: minutes,
      });
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to snooze habit'));
    }
  };

  const handleUnsnooze = async (habit: Habit) => {
    try {
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/unsnooze`);
      await fetchHabits();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to unsnooze habit'));
    }
  };

  // -- More menu (clear / mark fully done shortcuts) ------------------------

  const openMoreMenu = (event: React.MouseEvent<HTMLElement>, habit: Habit) => {
    setMoreMenuAnchor(event.currentTarget);
    setMoreMenuHabit(habit);
  };

  const closeMoreMenu = () => {
    setMoreMenuAnchor(null);
    setMoreMenuHabit(null);
  };

  const toggleSelected = (habitId: number, checked: boolean) => {
    setSelectedHabitIds((prev) => {
      if (checked) {
        return prev.includes(habitId) ? prev : [...prev, habitId];
      }
      return prev.filter((id) => id !== habitId);
    });
  };

  const openExportDialog = (ids: number[], title?: string) => {
    setExportHabitIds(ids);
    setExportDialogTitle(title);
    setExportDialogOpen(true);
  };

  const closeExportDialog = () => {
    setExportDialogOpen(false);
    setExportDialogTitle(undefined);
  };

  // -- Measurable progress rendering ----------------------------------------

  /** Period-scoped progress line + bar, from the server's period_progress.
   * Renders for measurable habits with a value axis or unit, and for ANY
   * non-opaque habit whose period spans more than a day ("40 / 60 minutes
   * this period · 20 remaining · period ends in 1 day"). Returns null where
   * the classic count chips already tell the whole story. */
  const measurableProgress = (habit: Habit, dense = false): React.ReactNode => {
    const pp = habit.period_progress;
    const kind = (habit.measurement_kind as string) || 'binary';
    if (!pp || kind === 'opaque') return null;
    const multiDay = periodSpanDays(pp.period_start, pp.period_end) > 1;
    const useValue = !!(pp.target_value && pp.target_value > 0);
    if (!multiDay && !(isMeasurableKind(kind) && (useValue || habit.unit_label))) {
      return null;
    }
    const done = useValue
      ? formatMeasureValue(pp.logged_value)
      : String(pp.logged_count);
    const total = useValue
      ? formatMeasureValue(pp.target_value as number)
      : String(pp.target_count);
    // unit_label is the ONLY display unit — never the import-provenance `unit`.
    const unit = habit.unit_label ? ` ${habit.unit_label}` : '';
    let line = `${done} / ${total}${unit}`;
    if (multiDay) {
      const remaining =
        useValue && pp.remaining_value != null
          ? formatMeasureValue(pp.remaining_value)
          : String(pp.remaining_count);
      line += ` this period · ${remaining} remaining · period ends in ${pp.days_left} ${
        pp.days_left === 1 ? 'day' : 'days'
      }`;
    }
    const percent = Math.max(
      0,
      Math.min(
        100,
        useValue
          ? (pp.logged_value / (pp.target_value as number)) * 100
          : (pp.logged_count / Math.max(1, pp.target_count)) * 100,
      ),
    );
    return (
      <Box sx={{ mb: dense ? 0 : 1 }}>
        <Typography variant={dense ? 'caption' : 'body2'} color="text.secondary">
          {line}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ height: 6, borderRadius: 3, mt: 0.5, maxWidth: 240 }}
        />
      </Box>
    );
  };

  /** OPAQUE habits: the stored value verbatim plus met/not-met — no unit, no
   * remaining arithmetic, no fraction language. The owner's private scheme
   * lives in the habit description, which the card already surfaces. */
  const opaqueValueDisplay = (habit: Habit, dense = false): React.ReactNode => {
    const pp = habit.period_progress;
    if (((habit.measurement_kind as string) || 'binary') !== 'opaque' || !pp) {
      return null;
    }
    const met = pp.logged_count >= pp.target_count;
    const multiDay = periodSpanDays(pp.period_start, pp.period_end) > 1;
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: dense ? 0 : 1 }}>
        <Typography variant={dense ? 'caption' : 'body2'}>
          {multiDay ? 'This period' : 'Today'}: {formatMeasureValue(pp.logged_value)}
        </Typography>
        <Chip
          size="small"
          label={met ? 'Met' : 'Not met'}
          color={met ? 'success' : 'default'}
          variant={met ? 'filled' : 'outlined'}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <PageShell maxWidth="wide">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="wide">
      <Box
        sx={[
          pageHeaderSx,
          {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          },
        ]}
      >
        <Typography variant="h3" component="h1">
          Habits
        </Typography>
        <Box display="flex" alignItems="center" gap={1.5}>
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            ariaLabel="habit view mode"
            isOverride={viewModeControls.isOverride}
            onUseDefault={viewModeControls.useDefault}
          />
          {viewMode === 'list' && (
            <>
              <Tooltip title="Choose columns">
                <IconButton
                  aria-label="Choose columns"
                  onClick={(event) => setColumnsMenuAnchor(event.currentTarget)}
                >
                  <ViewColumnIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
              >
                {HIDEABLE_COLUMNS.map((column) => (
                  <MenuItem key={column.key} onClick={() => toggleColumn(column.key)}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={isColumnVisible(column.key)}
                      readOnly
                      tabIndex={-1}
                      inputProps={{ 'aria-label': column.label }}
                      sx={{ pointerEvents: 'none' }}
                    />
                    <ListItemText>{column.label}</ListItemText>
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={resetColumns}>Reset columns</MenuItem>
              </Menu>
            </>
          )}
          {preferences && (
            <>
              <Tooltip title="Title click action">
                <IconButton
                  aria-label="Title click action"
                  onClick={(event) => setTitleActionMenuAnchor(event.currentTarget)}
                >
                  <AdsClickIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={titleActionMenuAnchor}
                open={Boolean(titleActionMenuAnchor)}
                onClose={() => setTitleActionMenuAnchor(null)}
              >
                {TITLE_CLICK_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    selected={option.value === titleClickAction}
                    onClick={() => {
                      preferences.setTyped('habit_title_click_action', option.value);
                      setTitleActionMenuAnchor(null);
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={openCreateDialog}
          >
            Add Habit
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Build consistency with differentiated daily, weekly, and monthly routines.
      </Typography>

      {/* Tag filter — wired to the ?tags=&tag_mode= query params so the
          filtering happens server-side (SQL), never in this component. */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={tagOptions}
          value={tagFilter}
          onChange={(_, value) => setTagFilter(normalizeTags(value))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Filter by tags" placeholder="focus, health" />
          )}
          sx={{ minWidth: 260 }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={tagMode}
          onChange={(_, value: 'any' | 'all' | null) => {
            if (value) setTagMode(value);
          }}
          aria-label="tag filter mode"
        >
          <ToggleButton value="any">Any</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, value: 'active' | 'archived' | null) => {
            if (value) setStatusFilter(value);
          }}
          aria-label="habit status filter"
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
        </ToggleButtonGroup>
        {/* SQL-side sorting — ?sort=&order=. '' = the list's default order. */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="habit-sort-label">Sort by</InputLabel>
          <Select
            labelId="habit-sort-label"
            label="Sort by"
            value={sortKey ?? ''}
            onChange={(e) => {
              const value = e.target.value as HabitSortKey | '';
              setSortKey(value || null);
              if (value) setSortOrder(defaultOrderFor(value));
            }}
          >
            <MenuItem value="">Default</MenuItem>
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {sortKey && (
          <Tooltip title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
            <IconButton
              size="small"
              aria-label={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
              onClick={() =>
                setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))
              }
            >
              {sortOrder === 'asc' ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
        {/* SQL-side filters — ?progress_today=&check_in=&q=. */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="habit-progress-filter-label">Progress today</InputLabel>
          <Select
            labelId="habit-progress-filter-label"
            label="Progress today"
            value={progressFilter}
            onChange={(e) =>
              setProgressFilter(e.target.value as HabitProgressTodayFilter | '')
            }
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="untouched">Untouched</MenuItem>
            <MenuItem value="partial">Partial</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="habit-checkin-filter-label">Check-in</InputLabel>
          <Select
            labelId="habit-checkin-filter-label"
            label="Check-in"
            value={checkInFilter}
            onChange={(e) =>
              setCheckInFilter(e.target.value as HabitCheckInFilter | '')
            }
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="enabled">Enabled</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search"
          placeholder="title or description"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {/* Saved presets — named sort/filter combinations persisted in the
          habits_filters namespace. Only rendered with an account-preferences
          provider mounted; provider-free mounts have nowhere to keep them. */}
      {preferences && (
        <Box
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
        >
          {filterPresets.map((preset) => (
            <Chip
              key={preset.id}
              label={preset.name}
              size="small"
              variant="outlined"
              onClick={() => applyFilterPreset(preset)}
              onDelete={() => deleteFilterPreset(preset.id)}
              deleteIcon={<CancelIcon aria-label={`Delete preset ${preset.name}`} />}
            />
          ))}
          <Button
            size="small"
            onClick={() => setPresetDialogOpen(true)}
            disabled={filterPresets.length >= HABITS_FILTER_PRESET_LIMIT}
          >
            Save preset
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {habits.length > 0 && selectedHabitIds.length > 0 && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedHabitIds.length} selected
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setBulkEditOpen(true)}
            >
              Edit selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => openExportDialog(selectedHabitIds, 'Export Selected Habits')}
            >
              Export
            </Button>
            <Button size="small" onClick={() => setSelectedHabitIds([])}>
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {habits.length === 0 ? (
        <Card>
          <CardContent sx={cardContentPaddingSx}>
            <Typography variant="h6" gutterBottom>
              No habits yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first habit to start tracking your daily routines.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Add Your First Habit
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={pageGridSpacing}>
          {habits.map((habit) => {
            const target = habit.target_count || 1;
            const completed = habit.today_completed_count || 0;
            const isFullyDone = completed >= target;
            const isSnoozed = !!habit.is_snoozed;
            const snoozedUntilMs = habit.snoozed_until
              ? new Date(habit.snoozed_until).getTime()
              : 0;
            const remainingMs = snoozedUntilMs - Date.now();
            const kind = (habit.measurement_kind as string) || 'binary';
            const isOpaque = kind === 'opaque';
            const hasChips =
              isMeasurableKind(kind) && quickIncrementValues(habit).length > 0;
            const progressNode = measurableProgress(habit);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={habit.id}>
                <Card
                  sx={{
                    height: '100%',
                    opacity: isSnoozed ? 0.65 : 1,
                    transition: 'opacity 200ms',
                  }}
                >
                  <CardContent sx={cardContentPaddingSx}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                      <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ minWidth: 0 }}>
                        <Checkbox
                          size="small"
                          checked={selectedHabitIds.includes(habit.id)}
                          onChange={(event) => toggleSelected(habit.id, event.target.checked)}
                          inputProps={{ 'aria-label': `Select ${habit.title}` }}
                          sx={{ mt: -0.5, ml: -1 }}
                        />
                        <HabitTitle
                          habit={habit}
                          action={titleClickAction}
                          variant="h6"
                          onActivate={handleTitleActivate}
                        />
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(habit)}
                            aria-label={`Edit ${habit.title}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {habit.archived_at ? (
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(habit)}
                              aria-label={`Restore ${habit.title}`}
                            >
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              onClick={() => handleArchive(habit)}
                              aria-label={`Archive ${habit.title}`}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setHabitToDelete(habit);
                              setDeleteDialogOpen(true);
                            }}
                            aria-label={`Delete ${habit.title}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {habit.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {habit.description}
                      </Typography>
                    )}

                    <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
                      <Chip
                        label={recurrenceLabel[habit.recurrence_type]}
                        color={recurrenceColor(habit.recurrence_type)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip label={`Every ${habit.recurrence_interval}`} size="small" />
                      <Chip label={`Target ${habit.target_count}`} size="small" />
                      {habit.archived_at ? (
                        <Chip
                          icon={<ArchiveIcon />}
                          label={`Archived ${new Date(habit.archived_at).toLocaleDateString()}`}
                          size="small"
                        />
                      ) : (
                        <Chip
                          label={habit.is_active ? 'Active' : 'Paused'}
                          size="small"
                          color={habit.is_active ? 'success' : 'default'}
                        />
                      )}
                      {target > 1 && !isOpaque && !progressNode && (
                        <Chip
                          label={`Today ${completed}/${target}`}
                          size="small"
                          color={isFullyDone ? 'success' : 'default'}
                          variant={isFullyDone ? 'filled' : 'outlined'}
                        />
                      )}
                      {isSnoozed && (
                        <Chip
                          icon={<SnoozeIcon />}
                          color="warning"
                          size="small"
                          label={`Snoozed · ${formatRemaining(remainingMs)}`}
                        />
                      )}
                    </Box>

                    {progressNode}
                    {opaqueValueDisplay(habit)}

                    {/* Action bar — Done / Snooze / History */}
                    <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center" mb={1}>
                      {isOpaque || hasChips ? (
                        <>
                          <HabitQuickLogChips
                            habit={habit}
                            onLogged={fetchHabits}
                            onError={setError}
                          />
                          {!isOpaque && (
                            <IconButton
                              size="small"
                              onClick={(e) => openMoreMenu(e, habit)}
                              aria-label="more habit actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      ) : target === 1 ? (
                        <Tooltip
                          title={
                            isFullyDone
                              ? 'Click to un-mark for today'
                              : 'Mark done for today'
                          }
                        >
                          <Button
                            variant={isFullyDone ? 'contained' : 'outlined'}
                            color={isFullyDone ? 'success' : 'primary'}
                            size="small"
                            startIcon={isFullyDone ? <CheckCircleIcon /> : <CheckIcon />}
                            onClick={() =>
                              isFullyDone ? handleClearToday(habit) : handleMarkComplete(habit)
                            }
                          >
                            {isFullyDone ? 'Done' : 'Mark Done'}
                          </Button>
                        </Tooltip>
                      ) : (
                        <ButtonGroup size="small" variant="outlined">
                          <Button
                            color={isFullyDone ? 'success' : 'primary'}
                            startIcon={isFullyDone ? <CheckCircleIcon /> : <CheckIcon />}
                            onClick={() => handleIncrement(habit)}
                            disabled={isFullyDone}
                          >
                            {isFullyDone ? `${target}/${target}` : `+1  (${completed}/${target})`}
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => openMoreMenu(e, habit)}
                            aria-label="more habit actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </Button>
                        </ButtonGroup>
                      )}

                      {isSnoozed ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<AlarmOffIcon />}
                          onClick={() => handleUnsnooze(habit)}
                        >
                          Unsnooze
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SnoozeIcon />}
                          endIcon={<ArrowDropDownIcon />}
                          onClick={(e) => openSnoozeMenu(e, habit)}
                        >
                          Snooze
                        </Button>
                      )}

                      <Tooltip title="Export">
                        <IconButton
                          size="small"
                          onClick={() => openExportDialog([habit.id], `Export ${habit.title}`)}
                          aria-label={`Export ${habit.title}`}
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Stats">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/habits/stats/${habit.id}`)}
                          aria-label={`View stats for ${habit.title}`}
                        >
                          <BarChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="History">
                        <IconButton
                          size="small"
                          onClick={() => setHistoryHabit(habit)}
                          aria-label={`View history for ${habit.title}`}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Created {new Date(habit.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Habit</TableCell>
                {isColumnVisible('recurrence') && <TableCell>Recurrence</TableCell>}
                {isColumnVisible('today') && <TableCell>Today</TableCell>}
                {isColumnVisible('status') && <TableCell>Status</TableCell>}
                {isColumnVisible('checkin') && <TableCell>Check-in</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {habits.map((habit) => {
                const target = habit.target_count || 1;
                const completed = habit.today_completed_count || 0;
                const isFullyDone = completed >= target;
                const isSnoozed = !!habit.is_snoozed;
                const snoozedUntilMs = habit.snoozed_until
                  ? new Date(habit.snoozed_until).getTime()
                  : 0;
                const remainingMs = snoozedUntilMs - Date.now();
                const kind = (habit.measurement_kind as string) || 'binary';
                const isOpaque = kind === 'opaque';
                const hasChips =
                  isMeasurableKind(kind) && quickIncrementValues(habit).length > 0;
                const progressNode = measurableProgress(habit, true);

                return (
                  <TableRow key={habit.id} hover sx={{ opacity: isSnoozed ? 0.65 : 1 }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedHabitIds.includes(habit.id)}
                        onChange={(event) => toggleSelected(habit.id, event.target.checked)}
                        inputProps={{ 'aria-label': `Select ${habit.title}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <HabitTitle
                        habit={habit}
                        action={titleClickAction}
                        variant="body2"
                        onActivate={handleTitleActivate}
                      />
                      {habit.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: 'block', maxWidth: 320 }}
                        >
                          {habit.description}
                        </Typography>
                      )}
                    </TableCell>
                    {isColumnVisible('recurrence') && (
                      <TableCell>
                        <Chip
                          label={recurrenceLabel[habit.recurrence_type]}
                          color={recurrenceColor(habit.recurrence_type)}
                          size="small"
                          variant="outlined"
                        />
                        {habit.recurrence_interval > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ×{habit.recurrence_interval}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible('today') && (
                      <TableCell>
                        {(isOpaque ? opaqueValueDisplay(habit, true) : progressNode) ?? (
                          <>
                            {completed}/{target}
                          </>
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible('status') && (
                      <TableCell>
                        {habit.archived_at ? (
                          <Chip
                            icon={<ArchiveIcon />}
                            size="small"
                            label={`Archived ${new Date(habit.archived_at).toLocaleDateString()}`}
                          />
                        ) : isSnoozed ? (
                          <Chip
                            icon={<SnoozeIcon />}
                            color="warning"
                            size="small"
                            label={formatRemaining(remainingMs)}
                          />
                        ) : (
                          <Chip
                            label={habit.is_active ? 'Active' : 'Paused'}
                            size="small"
                            color={habit.is_active ? 'success' : 'default'}
                          />
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible('checkin') && (
                      <TableCell>
                        {habit.check_in_enabled ? (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={
                              (habit.check_in_priority ?? 0) > 0
                                ? `On · P${habit.check_in_priority}`
                                : 'On'
                            }
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Box
                        display="flex"
                        gap={0.5}
                        justifyContent="flex-end"
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        {isOpaque || hasChips ? (
                          <>
                            <HabitQuickLogChips
                              habit={habit}
                              onLogged={fetchHabits}
                              onError={setError}
                            />
                            {!isOpaque && (
                              <IconButton
                                size="small"
                                onClick={(e) => openMoreMenu(e, habit)}
                                aria-label="more habit actions"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        ) : target === 1 ? (
                          <Button
                            size="small"
                            variant={isFullyDone ? 'contained' : 'outlined'}
                            color={isFullyDone ? 'success' : 'primary'}
                            startIcon={isFullyDone ? <CheckCircleIcon /> : <CheckIcon />}
                            onClick={() =>
                              isFullyDone ? handleClearToday(habit) : handleMarkComplete(habit)
                            }
                          >
                            {isFullyDone ? 'Done' : 'Mark Done'}
                          </Button>
                        ) : (
                          <ButtonGroup size="small" variant="outlined">
                            <Button
                              color={isFullyDone ? 'success' : 'primary'}
                              startIcon={isFullyDone ? <CheckCircleIcon /> : <CheckIcon />}
                              onClick={() => handleIncrement(habit)}
                              disabled={isFullyDone}
                            >
                              {isFullyDone ? `${target}/${target}` : '+1'}
                            </Button>
                            <Button
                              size="small"
                              onClick={(e) => openMoreMenu(e, habit)}
                              aria-label="more habit actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </Button>
                          </ButtonGroup>
                        )}
                        {isSnoozed ? (
                          <Tooltip title="Unsnooze">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleUnsnooze(habit)}
                              aria-label={`Unsnooze ${habit.title}`}
                            >
                              <AlarmOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Snooze">
                            <IconButton
                              size="small"
                              onClick={(e) => openSnoozeMenu(e, habit)}
                              aria-label={`Snooze ${habit.title}`}
                            >
                              <SnoozeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Stats">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/habits/stats/${habit.id}`)}
                            aria-label={`View stats for ${habit.title}`}
                          >
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="History">
                          <IconButton
                            size="small"
                            onClick={() => setHistoryHabit(habit)}
                            aria-label={`View history for ${habit.title}`}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(habit)}
                            aria-label={`Edit ${habit.title}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {habit.archived_at ? (
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(habit)}
                              aria-label={`Restore ${habit.title}`}
                            >
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              onClick={() => handleArchive(habit)}
                              aria-label={`Archive ${habit.title}`}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setHabitToDelete(habit);
                              setDeleteDialogOpen(true);
                            }}
                            aria-label={`Delete ${habit.title}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snooze duration menu — single instance reused across all cards. */}
      <Menu
        anchorEl={snoozeMenuAnchor}
        open={Boolean(snoozeMenuAnchor)}
        onClose={closeSnoozeMenu}
      >
        {SNOOZE_PRESETS.map((preset) => (
          <MenuItem key={preset.minutes} onClick={() => handleSnooze(preset.minutes)}>
            {preset.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Per-card overflow menu for discrete habits (target > 1). */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={closeMoreMenu}
      >
        <MenuItem
          onClick={() => {
            if (moreMenuHabit) handleMarkComplete(moreMenuHabit);
            closeMoreMenu();
          }}
        >
          Mark fully done
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (moreMenuHabit) handleClearToday(moreMenuHabit);
            closeMoreMenu();
          }}
        >
          Clear today
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editHabit ? 'Edit Habit' : 'Add Habit'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <TextField
            margin="dense"
            fullWidth
            required
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Recurrence Type</InputLabel>
            <Select
              value={formData.recurrence_type}
              label="Recurrence Type"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  recurrence_type: e.target.value as HabitRecurrence,
                }))
              }
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                label="Recurrence Interval"
                value={formData.recurrence_interval}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    recurrence_interval: Number(e.target.value),
                  }))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                label="Target Count"
                value={formData.target_count}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, target_count: Number(e.target.value) }))
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Measurement
          </Typography>
          <FormControl fullWidth sx={{ mb: 0.5 }}>
            <InputLabel>Measurement kind</InputLabel>
            <Select
              value={formData.measurement_kind}
              label="Measurement kind"
              onChange={(e) => {
                const nextKind = e.target.value as HabitMeasurementKind;
                // OPAQUE can't carry debt (no arithmetic on opaque values,
                // ever — the server 422s it), so a chosen debt mode resets
                // to none, with the reason surfaced under the mode select.
                const resetsDebtMode =
                  nextKind === 'opaque' &&
                  formData.backfill_mode === 'debt_carry_forward';
                setFormData((prev) => ({
                  ...prev,
                  measurement_kind: nextKind,
                  ...(nextKind === 'opaque' &&
                  prev.backfill_mode === 'debt_carry_forward'
                    ? { backfill_mode: 'none' as HabitBackfillModeValue }
                    : {}),
                }));
                setDebtModeResetNote(resetsDebtMode);
                // A kind switch invalidates any pending conversion preview.
                setConversionPreview(null);
                setConversionError(null);
              }}
            >
              {MEASUREMENT_KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {
                MEASUREMENT_KIND_OPTIONS.find(
                  (option) => option.value === formData.measurement_kind,
                )?.helper
              }
            </FormHelperText>
          </FormControl>
          {formData.measurement_kind !== 'binary' && (
            <TextField
              fullWidth
              margin="dense"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              label="Target value"
              helperText="The measured amount that counts as fully done for one period."
              value={formData.target_value}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, target_value: e.target.value }))
              }
            />
          )}
          {/* OPAQUE deliberately gets none of the interpretive extras below —
              no unit, no batches, no quick chips, no conversion helper. */}
          {isMeasurableKind(formData.measurement_kind) && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Unit label"
                    helperText="Shown after values (e.g. minutes, recitations)."
                    value={formData.unit_label}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, unit_label: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    type="number"
                    inputProps={{ min: 1 }}
                    label="Batch size"
                    helperText="One batch equals this many units (e.g. one mala = 108)."
                    value={formData.batch_size}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, batch_size: e.target.value }))
                    }
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 1 }}>
                <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
                  {formData.quick_increments.map((value, index) => (
                    <Chip
                      key={`${value}-${index}`}
                      size="small"
                      label={`+${formatMeasureValue(value)}`}
                      onDelete={() =>
                        setFormData((prev) => ({
                          ...prev,
                          quick_increments: prev.quick_increments.filter(
                            (_, i) => i !== index,
                          ),
                        }))
                      }
                    />
                  ))}
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Add +N"
                    inputProps={{ min: 0, step: 'any', 'aria-label': 'New quick increment' }}
                    value={quickIncrementDraft}
                    onChange={(e) => setQuickIncrementDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addQuickIncrement();
                      }
                    }}
                    sx={{ width: 110 }}
                  />
                  <Button
                    size="small"
                    onClick={addQuickIncrement}
                    disabled={formData.quick_increments.length >= 8}
                  >
                    Add
                  </Button>
                </Box>
                <FormHelperText>
                  Up to 8 one-tap logging chips (e.g. +9, +12, +27, +108).
                </FormHelperText>
              </Box>
              {conversionOffered && (
                <Box
                  sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Convert existing history
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    One-time: rewrites every logged value into the new units. My
                    existing logged values represent multiples of…
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <TextField
                      size="small"
                      type="number"
                      label="Multiplier"
                      inputProps={{ min: 0, step: 'any' }}
                      value={conversionMultiplier}
                      onChange={(e) => {
                        setConversionMultiplier(e.target.value);
                        setConversionPreview(null);
                      }}
                      sx={{ width: 130 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={conversionLoading}
                      onClick={() => runConversion(true)}
                    >
                      Preview conversion
                    </Button>
                  </Box>
                  {conversionError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {conversionError}
                    </Alert>
                  )}
                  {conversionPreview && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" gutterBottom>
                        {conversionPreview.affected_log_rows} of{' '}
                        {conversionPreview.total_log_rows} log rows will change.
                      </Typography>
                      <TableContainer>
                        <Table
                          size="small"
                          sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Before</TableCell>
                              <TableCell align="right">After</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {conversionPreview.samples.map((sample) => (
                              <TableRow key={sample.date}>
                                <TableCell>{sample.date}</TableCell>
                                <TableCell align="right">
                                  {sample.before_value != null
                                    ? formatMeasureValue(sample.before_value)
                                    : sample.before_count}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMeasureValue(sample.after_value)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box display="flex" gap={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={conversionLoading}
                          onClick={() => runConversion(false)}
                        >
                          Convert
                        </Button>
                        <Button
                          size="small"
                          disabled={conversionLoading}
                          onClick={cancelConversion}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {conversionDone && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Converted — {conversionDone.affected_log_rows} log rows rewritten.
                </Alert>
              )}
            </>
          )}

          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={formData.tags}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, tags: normalizeTags(value) }))
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Tags"
                placeholder="focus, health, evening"
              />
            )}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Check-In
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.check_in_enabled}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, check_in_enabled: e.target.checked }))
                }
              />
            }
            label="Include in the check-in queue"
          />
          {formData.check_in_enabled && (
            <>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                margin="dense"
                label="Check-in priority (higher shows first)"
                value={formData.check_in_priority}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    check_in_priority: Number(e.target.value),
                  }))
                }
              />
              {editHabit ? (
                <CheckInWindowsEditor habitId={editHabit.id} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Save the habit, then reopen it to add time windows. With no window it&apos;s
                  eligible all day.
                </Typography>
              )}
              <FormControl fullWidth margin="dense" sx={{ mt: 1 }}>
                <InputLabel id="score-preset-select-label">Score preset</InputLabel>
                <Select
                  labelId="score-preset-select-label"
                  label="Score preset"
                  value={formData.score_preset_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      score_preset_id:
                        e.target.value === '' ? '' : Number(e.target.value),
                    }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {scorePresets.map((preset) => (
                    <MenuItem key={preset.id} value={preset.id}>
                      {preset.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Assigning stamps the preset&apos;s answers onto this habit; existing
                  custom answers are never deleted.
                </FormHelperText>
              </FormControl>
              {editHabit && (
                <>
                  <Button
                    size="small"
                    sx={{ mt: 0.5 }}
                    onClick={() => setOptionsEditorOpen(true)}
                  >
                    Customize responses…
                  </Button>
                  <CheckInOptionsEditor
                    habitId={editHabit.id}
                    habitTitle={editHabit.title}
                    open={optionsEditorOpen}
                    onClose={() => setOptionsEditorOpen(false)}
                  />
                </>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                Catch-up &amp; carry-forward
              </Typography>
              <FormControl fullWidth margin="dense">
                <InputLabel id="backfill-mode-select-label">If I miss a day</InputLabel>
                <Select
                  labelId="backfill-mode-select-label"
                  label="If I miss a day"
                  value={formData.backfill_mode}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      backfill_mode: e.target.value as HabitBackfillModeValue,
                    }));
                    // A deliberate pick supersedes the auto-reset note.
                    setDebtModeResetNote(false);
                  }}
                >
                  <MenuItem value="none">Missed is missed</MenuItem>
                  <MenuItem value="retroactive_log">Let me log it late</MenuItem>
                  <MenuItem
                    value="debt_carry_forward"
                    disabled={formData.measurement_kind === 'opaque'}
                  >
                    Carry it forward as owed
                  </MenuItem>
                </Select>
                {formData.measurement_kind === 'opaque' && (
                  <FormHelperText>Opaque values never get arithmetic</FormHelperText>
                )}
                {debtModeResetNote && (
                  <FormHelperText>
                    Switched to &quot;Missed is missed&quot; — owed amounts need
                    arithmetic, and opaque values never get any.
                  </FormHelperText>
                )}
              </FormControl>
              {formData.backfill_mode !== 'none' && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    type="number"
                    label="How far back (days)"
                    placeholder={
                      formData.backfill_mode === 'retroactive_log' ? '7' : '35'
                    }
                    inputProps={{ min: 1, max: 365 }}
                    helperText="Empty or 0 uses the default."
                    value={formData.backfill_reach_days}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        backfill_reach_days: e.target.value,
                      }))
                    }
                    sx={{ width: 220 }}
                  />
                </Box>
              )}
              {formData.backfill_mode === 'debt_carry_forward' && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    gutterBottom
                  >
                    Owed periods
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    aria-label="Owed periods"
                    value={formData.carryover_policy}
                    onChange={(_, value) => {
                      if (value) {
                        setFormData((prev) => ({
                          ...prev,
                          carryover_policy: value as HabitCarryoverPolicyValue,
                        }));
                      }
                    }}
                  >
                    <ToggleButton value="merge">Combine</ToggleButton>
                    <ToggleButton value="stack">Keep separate</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saveLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saveLoading}>
            {saveLoading ? <CircularProgress size={20} /> : editHabit ? 'Save Changes' : 'Add Habit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Habit</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            Are you sure you want to delete "{habitToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={presetDialogOpen}
        onClose={() => setPresetDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Save filter preset</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText sx={{ mb: 1 }}>
            Saves the current sort and filters as a one-click preset.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Preset name"
            value={presetNameDraft}
            onChange={(e) => setPresetNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveFilterPreset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveFilterPreset}
            disabled={!presetNameDraft.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {historyHabit && (
        <HabitHistoryDialog
          habit={historyHabit}
          open={!!historyHabit}
          onClose={() => setHistoryHabit(null)}
          onChange={fetchHabits}
        />
      )}

      <HabitExportDialog
        open={exportDialogOpen}
        habitIds={exportHabitIds}
        title={exportDialogTitle}
        onClose={closeExportDialog}
      />

      <HabitBulkEditDialog
        open={bulkEditOpen}
        habitIds={selectedHabitIds}
        habits={habits}
        onClose={() => setBulkEditOpen(false)}
        onApplied={fetchHabits}
      />
    </PageShell>
  );
};

export default Habits;
